/**
 * Device-side AIP author signing for on-chain posts (use_client_signing).
 *
 * The server (PostWorkflow) builds the byte-final bSocial ops of a public post
 * and asks the author's device to sign them as the post's AIP author tuple; the
 * platform still funds the tx and countersigns. The device never signs bytes it
 * was handed: it checks the ops are a Treechat post by this user for the named
 * post, then builds the canonical AIP message itself (bsv/src/bsv/aipSign.ts
 * aipMessageForOps: 0x6a, every op's UTF-8 bytes, then the tuple's '|').
 * See docs/user-aip-signing-architecture.md.
 */
import { BSM, PrivateKey } from '@bsv/sdk'

const B_PROTOCOL_ADDRESS = '19HxigV4QyBv3tHpQVcUEQyq1pzZVdoAut'
const MAP_PROTOCOL_ADDRESS = '1PuQa7K62MiKCtssSLKy1kh56WWU7MtUR5'

// The MAP keys a Treechat post carries (bsv/src/bsv/bsv-lock/bSocialPost.js and
// LoadPostContextActivity). Anything else is refused rather than signed.
const ALLOWED_MAP_KEYS = new Set([
    'app',
    'type',
    'context',
    'tx',
    'username',
    'treechat_msg_id',
    'treechat_thread_id',
    'treechat_created_at',
])

export interface PostAipExpectation {
    username: string
    answerId: string
}

/** Returns why ops are not a post this user may sign for answerId, or null. */
export function postAipOpsProblem(ops: unknown, expected: PostAipExpectation): string | null {
    if (!Array.isArray(ops) || !ops.every(op => typeof op === 'string')) {
        return 'ops must be strings'
    }
    if (
        ops[0] !== B_PROTOCOL_ADDRESS ||
        ops[2] !== 'text/markdown' ||
        ops[3] !== 'UTF-8' ||
        ops[4] !== '|' ||
        ops[5] !== MAP_PROTOCOL_ADDRESS ||
        ops[6] !== 'SET'
    ) {
        return 'ops are not a bSocial text post'
    }

    const pairs = ops.slice(7)
    if (pairs.length === 0 || pairs.length % 2 !== 0) {
        return 'MAP fields are not key/value pairs'
    }
    const fields = new Map<string, string[]>()
    for (let i = 0; i < pairs.length; i += 2) {
        const key = pairs[i]
        if (!ALLOWED_MAP_KEYS.has(key)) return `unexpected MAP field ${key}`
        fields.set(key, [...(fields.get(key) || []), pairs[i + 1]])
    }
    const only = (key: string) => {
        const values = fields.get(key)
        return values?.length === 1 ? values[0] : undefined
    }

    // Production posts use 'treechat'; other deploys suffix it.
    if (!/^treechat(_[a-z0-9]+)?$/.test(only('app') || '')) return 'not a Treechat post'
    if (only('type') !== 'post') return 'not a post'
    if (only('username') !== expected.username) return 'post is not by this user'
    if (only('treechat_msg_id') !== String(expected.answerId)) return 'post id does not match'
    // A reply is context=tx followed by tx=<parent txid>; nothing else uses tx.
    const tx = fields.get('tx') || []
    if (fields.has('context') || tx.length > 0) {
        const replyShaped =
            only('context') === 'tx' && tx.length === 1 && /^[0-9a-f]{64}$/.test(tx[0])
        if (!replyShaped) return 'malformed reply context'
    }
    return null
}

/** The canonical AIP message for ops (matches aipMessageForOps on the server). */
export function postAipMessage(ops: string[]): number[] {
    const encoder = new TextEncoder()
    return [0x6a, ...ops.flatMap(op => Array.from(encoder.encode(op))), 0x7c]
}

/** Base64 compact Bitcoin Signed Message signature over postAipMessage(ops). */
export function signPostAipOps(ops: string[], paymentKeyWif: string): string {
    return BSM.sign(postAipMessage(ops), PrivateKey.fromWif(paymentKeyWif), 'base64') as string
}

// ============================================================================
// Local posting intents
// ============================================================================
//
// The device only signs posts it wrote itself: when it creates or edits a post
// it records the post id and a digest of the content it sent, and a post_aip
// request must name one of those posts with content matching one of its
// digests. A compromised server can't obtain author signatures for posts this
// device never submitted, or for content it didn't send.

type IntentStorage = Pick<Storage, 'getItem' | 'setItem'>
type PostAipIntents = Record<string, { digests: string[]; at: number }>

const INTENT_TTL_MS = 24 * 60 * 60 * 1000
const MAX_INTENTS = 200
const MAX_DIGESTS_PER_POST = 8

const intentKey = (userId: string) => `treechat:post-aip-intents:${userId}`

const defaultStorage = (): IntentStorage | null => {
    try {
        return globalThis.localStorage ?? null
    } catch {
        return null
    }
}

const readIntents = (storage: IntentStorage, userId: string): PostAipIntents => {
    try {
        const parsed = JSON.parse(storage.getItem(intentKey(userId)) || '{}')
        return parsed && typeof parsed === 'object' ? parsed : {}
    } catch {
        return {}
    }
}

/**
 * The post body as the server stores and publishes it (Answer#format_content and
 * #display_content): the same /&nbsp;+/ replacement (each entity, with any
 * extra semicolons, becomes one space), then a Ruby-style strip.
 */
export function normalizePostContent(content: string): string {
    return (
        content
            // Posts go up as multipart form data, where browsers send every line break as CRLF:
            // the server's copy (what it asks this device to sign) has \r\n where the text this
            // device recorded has \n.
            .replace(/\r\n?/g, '\n')
            .replace(/&nbsp;+/g, ' ')
            .replace(/^[\0\t\n\v\f\r ]+/, '')
            .replace(/[\0\t\n\v\f\r ]+$/, '')
    )
}

export async function postContentDigest(content: string): Promise<string> {
    const bytes = new TextEncoder().encode(normalizePostContent(content))
    const digest = await crypto.subtle.digest('SHA-256', bytes)
    return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('')
}

/** Records that this device submitted content for answerId (create or edit). */
export async function recordPostAipIntent(
    userId: string,
    answerId: string,
    content: string,
    storage: IntentStorage | null = defaultStorage(),
    now = Date.now(),
): Promise<void> {
    if (!storage || !userId || !answerId || typeof content !== 'string') return
    const digest = await postContentDigest(content)
    try {
        const intents = readIntents(storage, userId)
        const previous = intents[answerId]?.digests || []
        intents[answerId] = {
            digests: [...previous.filter(d => d !== digest), digest].slice(-MAX_DIGESTS_PER_POST),
            at: now,
        }
        const kept = Object.entries(intents)
            .filter(([, intent]) => now - intent.at < INTENT_TTL_MS)
            .sort(([, a], [, b]) => b.at - a.at)
            .slice(0, MAX_INTENTS)
        storage.setItem(intentKey(userId), JSON.stringify(Object.fromEntries(kept)))
    } catch {
        // Storage full or blocked: this post publishes platform-signed instead.
    }
}

/** Why this device must not sign content for answerId, or null. */
export async function postAipIntentProblem(
    userId: string,
    answerId: string,
    content: string,
    storage: IntentStorage | null = defaultStorage(),
    now = Date.now(),
): Promise<string | null> {
    if (!storage) return 'no local record of this post'
    const intent = readIntents(storage, userId)[answerId]
    if (!intent || now - intent.at >= INTENT_TTL_MS) return 'post was not written on this device'
    const digest = await postContentDigest(content)
    return intent.digests.includes(digest)
        ? null
        : 'post content differs from what this device sent'
}
